import { useState } from "react";

import { ArrowDownIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import {
  buildTreeFromArray,
  getAdminAreaCriteria,
  getAttributeFilters
} from "@/pages/analysisHistory/utils";
import { DatasetHistoryAnalysisItem } from "@/types/analysisHistory";

import { twMerge } from "tailwind-merge";

import { DatasetCardHeader } from "../dataset/datasetCard/datasetCardHeader";
import { DatasetCardSpatialInfo } from "../dataset/datasetCard/datasetCardSpatialInfo";
import Divider from "../ui/Divider";
import { DetailAdminTree } from "./DetailAdminTree";

type Props = {
  data: DatasetHistoryAnalysisItem;
  expanded: boolean;
  handleExpandCollapse: (data: DatasetHistoryAnalysisItem) => void;
};

export const DatasetCard = ({
  data,
  expanded,
  handleExpandCollapse
}: Props) => {
  const attributeFilters = getAttributeFilters(data);
  const [expandedAdm, setExpandedAdm] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(false);
  const adminTree = buildTreeFromArray(getAdminAreaCriteria(data));

  return (
    <div
      className={twMerge(
        "group rounded-lg bg-white",
        !expanded && "border-none"
      )}
    >
      <button
        type="button"
        className={twMerge(
          "flex flex-col font-sans border-[1px] rounded-lg p-4 gap-2 min-h-[114px] cursor-pointer justify-between relative transition-colors w-full z-10",
          expanded ? "bg-teal-50 border-teal-200" : "bg-white border-gainsboro",
          "hover:bg-teal-50 hover:border-teal-200"
        )}
        onClick={() => handleExpandCollapse(data)}
      >
        <DatasetCardHeader
          showLogo
          rightIcon={
            <ArrowDownIcon
              className={twMerge(
                "transition-transform fill-txtBody",
                expanded && "rotate-180 fill-teal-700"
              )}
            />
          }
          datasetName={data.display_name}
          logoUrl={data.logo_asset_url}
          shortDescription={data.short_desc}
        />
        <div className="flex flex-col gap-1">
          <Divider
            className="bg-gainsboro w-full h-[1px]"
            lineClassName={twMerge(
              "group-hover:border-teal-200",
              expanded && "border-teal-200"
            )}
          />
          <DatasetCardSpatialInfo
            spatialType={data.spatial_aggregation_type}
            countryId={data.country_id}
            areaRegion={data.region}
            rightContent={
              <span className="text-[10px] text-grey">
                {data.source === "bvt" ? "Bvarta Data" : "User Data"}
              </span>
            }
          />
        </div>
      </button>
      {/* Expanded content */}
      {expanded && (
        <div className="p-4 px-2 text-[11px] text-txtHeading max-h-[500px] overflow-y-auto border border-gainsboro border-t-0 rounded-b-lg mt-[-0.5rem]">
          <div className="flex flex-col gap-2">
            <div className="bg-white w-full border border-whisper rounded-lg">
              {/* header */}
              <button
                type="button"
                onClick={() => setExpandedAdm(!expandedAdm)}
                className={cn(
                  "flex items-center justify-between w-full bg-white-smoke p-2 rounded-t-lg",
                  !expandedAdm && "rounded-b-lg"
                )}
              >
                <p className="font-semibold">Adm Area Selection</p>
                <ArrowDownIcon
                  className={twMerge(
                    "transition-transform fill-txtBody",
                    expandedAdm && "rotate-180 fill-teal-700"
                  )}
                />
              </button>
              {expandedAdm && (
                <div className="flex flex-col gap-0 p-2 overflow-y-auto">
                  {adminTree.length > 0 ? (
                    <>
                      <p className="font-semibold mb-2">{adminTree[0]?.name}</p>
                      <div className="pr-2 space-y-1">
                        <DetailAdminTree nodes={adminTree[0]?.children || []} />
                      </div>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              )}
            </div>
            <div className="bg-white w-full border border-whisper rounded-lg">
              {/* header */}
              <button
                type="button"
                onClick={() => setExpandedFilter(!expandedFilter)}
                className={cn(
                  "flex items-center justify-between w-full bg-white-smoke p-2 rounded-t-lg",
                  !expandedFilter && "rounded-b-lg"
                )}
              >
                <p className="font-semibold">Attribute Filter</p>
                <ArrowDownIcon
                  className={twMerge(
                    "transition-transform fill-txtBody",
                    expandedFilter && "rotate-180 fill-teal-700"
                  )}
                />
              </button>
              {expandedFilter && (
                <div className="flex flex-col gap-0 p-2 overflow-y-auto">
                  {attributeFilters.length > 0 ? (
                    attributeFilters.map((item) => (
                      <div key={item.label} className="flex flex-col gap-0.5">
                        <span className="font-semibold">{item.label}</span>
                        <span className="text-neutral-600">{item.value}</span>
                      </div>
                    ))
                  ) : (
                    <span>-</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
